import { DebounceSettings, debounce } from 'lodash'
import { destroyUserpilot, trackUserpilot } from './userpilot'
import { firebaseAnalytics, firebaseController, trackFirebaseAnalyticsUser } from './firebase'
import { fullstoryController, trackFullstoryUser, untrackFullstory } from './fullstory'
import { googleAnalyticsController, trackGoogleAnalyticsUser } from './googleAnalytics'
import { intercomController, trackIntercomUser, unTrack as unTrackIntercom } from './intercom'
import { mixpanelController, trackMixpanelUser } from './mixpanel'

import { Auth0User } from 'models/auth'
import { PlatformUser } from 'models/user'
import { logger } from 'utils/logging'
import { runProvider } from './runProvider'
import { withPfFlowVersion } from './pfFlowVersion'
import { trackSentryUser } from './sentry'

export { AnalyticsEvent } from './AnalyticsEvent'

/** Track the user to all analytics services [firebase, firebase-analytics, google-analytics, mixpanel, sentry, intercom, fullstory] */
export const trackUser = (user: Auth0User | PlatformUser) => {
  if (user && 'sub' in user) {
    runProvider('firebase', 'trackUser', () => trackFirebaseAnalyticsUser(user))
    runProvider('googleAnalytics', 'trackUser', () => trackGoogleAnalyticsUser(user))
    runProvider('sentry', 'trackUser', () => trackSentryUser(user))
  }

  runProvider('mixpanel', 'trackUser', () => trackMixpanelUser(user))
  runProvider('intercom', 'trackUser', () => trackIntercomUser(user))
  runProvider('userpilot', 'trackUser', () => trackUserpilot(user))
  runProvider('fullstory', 'trackUser', () => trackFullstoryUser(user))
}

/** Untrack the user */
export const unTrackUser = () => {
  runProvider('intercom', 'unTrackUser', () => unTrackIntercom())
  runProvider('userpilot', 'unTrackUser', () => destroyUserpilot())
  runProvider('fullstory', 'unTrackUser', () => untrackFullstory())
}

/** Mixpanel's encoder, like JSON.stringify, sends a Set or a Map as `{}`, so top-level ones go out as a list / an object. */
const withSerializableCollections = (body: object): Record<string, unknown> => {
  const entries = Object.entries(body)
  if (!entries.some(([, value]) => value instanceof Set || value instanceof Map)) return body as Record<string, unknown>
  return Object.fromEntries(entries.map(([key, value]) => [
    key,
    value instanceof Set ? Array.from(value) : value instanceof Map ? Object.fromEntries(value) : value,
  ]))
}

export interface AnalyticsEventOptions {
  /** Sent to Mixpanel at once by `sendBeacon` instead of the batch queue, so it survives the page closing. */
  transport?: 'beacon'
}

/** Logs an analytics event into analytics services  [firebase, firebase-analytics, google-analytics, mixpanel, intercom, fullstory] */
export const logAnalyticsEvent = <T extends object>(name: string, rawBody: T, options?: AnalyticsEventOptions) => {
  // Marks the event with the purchase flow it came from, when one is active.
  // Done here so that the shared components a flow hosts — upload widgets, the
  // preferred-payment switch, the staging popups — are marked too; they fire
  // their own events and know nothing about who is hosting them.
  const body = withSerializableCollections(withPfFlowVersion(rawBody))

  // Echoed for the developer before the fan-out: every provider here is a no-op
  // without its SDK configured, so on localhost an event was previously
  // impossible to observe at all. `logger.info` is console-only on
  // localhost/development and never reaches Sentry, so this costs production
  // nothing. Once per EVENT, not once per provider.
  logger.info(`analytics: ${name}`, body, { context: { component: 'analytics', action: 'logAnalyticsEvent' } })

  runProvider('firebase', name, () => {
    if (firebaseController.analytics) firebaseAnalytics.logEvent(firebaseController.analytics, name, body)
  })
  runProvider('googleAnalytics', name, () => googleAnalyticsController.instance?.track(name, body))
  runProvider('mixpanel', name, () => {
    if (options?.transport === 'beacon') mixpanelController.instance?.track(name, body, { transport: 'sendBeacon', send_immediately: true })
    else mixpanelController.instance?.track(name, body)
  })
  runProvider('intercom', name, () => intercomController.instance && intercomController.instance('trackEvent', name, body))
  runProvider('fullstory', name, () => fullstoryController.instance?.('trackEvent', { name, properties: body }))
}

/** Higher order function generating debounced logAnalyticsEvent function based on parameters */
export const getDebouncedLogAnalyticsEvent = (debounceTimeout: number, options: DebounceSettings) => debounce(logAnalyticsEvent, debounceTimeout, options)

/**
 * Debounced logAnalyticsEvent function with default debounce parameters.   
 * *(debounceTimeout: 500ms, maxWait: 2000ms, leading: true)*   
 *    
 * Debounced function with custom debounce parameters can be generated by **getDebouncedAnalyticsEvent**
*/
export const standardDebouncedLogAnalyticsEvent = getDebouncedLogAnalyticsEvent(500, { maxWait: 2000, leading: true })
